/**
 * Sentry browser SDK init for the LetsLoop website (Vite + React).
 *
 * Gating contract:
 * - Reads `import.meta.env.VITE_SENTRY_DSN` (Vite exposes only `VITE_*` vars to the client).
 * - If the DSN is unset we no-op so deploys succeed before Sentry is provisioned.
 * - Environment label comes from `VITE_SENTRY_ENVIRONMENT`, falling back to `import.meta.env.MODE`.
 *
 * NOTE: Do NOT commit a real DSN. The DSN must arrive via env at build/runtime.
 */
import * as Sentry from "@sentry/react";

let initialized = false;

export function initSentry(): void {
  if (initialized) return;

  const dsn =
    typeof import.meta !== "undefined" &&
    (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
      ? (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env
          .VITE_SENTRY_DSN
      : undefined;

  if (!dsn) {
    // No DSN — gracefully no-op (deploys must succeed without Sentry provisioned).
    return;
  }

  const env =
    (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env
      .VITE_SENTRY_ENVIRONMENT ??
    (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env.MODE ??
    "production";

  Sentry.init({
    dsn,
    environment: env,
    // Conservative defaults: errors only, no perf or replay until we evaluate quota.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });

  initialized = true;
}
